import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Pencil, Search, Trash2 } from "lucide-react";
import { dashboardToast } from "../../../utils/dashboardToast";
import ContentLoader from "../../ContentLoader";
import RecordDetailModal from "../../RecordDetailModal";
import TableSortSelect from "../../TableSortSelect";
import SettingsPanelActions from "./SettingsPanelActions";
import {
  Alert,
  Button,
  Input,
  Modal,
  Pagination as UiPagination,
  Switch,
  Textarea,
} from "../../ui";
import { TABLE_SORT } from "../../../utils/tableSort";
import {
  getBusinessTypeList,
  addOrUpdateBusinessType,
  deleteBusinessType,
} from "../../../services/businessApi";

const EmptyCell = () => <span className="app-td__empty">—</span>;

const UserTypesPanel = () => {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: 0,
    name: "",
    description: "",
    isActive: true,
  });
  const [businessTypes, setBusinessTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [businessTypeToDelete, setBusinessTypeToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewBusinessType, setViewBusinessType] = useState(null);
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);
  const [searchTerm, setSearchTerm] = useState("");
  const dataFetchedRef = useRef(false);

  const [apiPageInfo, setApiPageInfo] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
  });

  const fetchBusinessTypes = useCallback(
    async (page = 1, pageSize = 10, sort = sortKey, search = searchTerm) => {
      setLoading(true);
      setError(null);
      try {
        const result = await getBusinessTypeList(page, pageSize, sort, search);
        setBusinessTypes(result.items || []);
        setApiPageInfo({
          currentPage: result.pageNumber || page,
          pageSize: result.pageSize || pageSize,
          totalPages: Math.max(
            1,
            Math.ceil((result.totalCount || 0) / (result.pageSize || pageSize))
          ),
          totalCount: result.totalCount || 0,
        });
      } catch (err) {
        console.error("Error fetching user types:", err);
        setError("Failed to fetch user types. Please try again.");
        setBusinessTypes([]);
      } finally {
        setLoading(false);
      }
    },
    [sortKey, searchTerm]
  );

  useEffect(() => {
    fetchBusinessTypes(apiPageInfo.currentPage, apiPageInfo.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dataFetchedRef.current) {
      dataFetchedRef.current = true;
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      fetchBusinessTypes(1, apiPageInfo.pageSize, sortKey, searchTerm);
    }, 300);

    return () => window.clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const goToPage = (page) => {
    const newPage = Math.min(Math.max(page, 1), apiPageInfo.totalPages);
    if (newPage !== apiPageInfo.currentPage) {
      fetchBusinessTypes(newPage, apiPageInfo.pageSize);
    }
  };

  const handleSortChange = (nextSort) => {
    setSortKey(nextSort);
    fetchBusinessTypes(1, apiPageInfo.pageSize, nextSort, searchTerm);
  };

  const handleShow = useCallback((businessType = null) => {
    if (businessType) {
      setFormData({
        id: businessType.id || 0,
        name: businessType.name || "",
        description: businessType.description || "",
        isActive: businessType.isActive !== undefined ? businessType.isActive : true,
      });
    } else {
      setFormData({ id: 0, name: "", description: "", isActive: true });
    }
    setShowModal(true);
  }, []);

  const handleClose = useCallback(() => setShowModal(false), []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!formData.name.trim()) {
        dashboardToast.error("User type name is required");
        return;
      }

      try {
        setLoading(true);
        await addOrUpdateBusinessType(formData);
        handleClose();
        dashboardToast.success(
          formData.id > 0 ? "User type updated successfully" : "User type created successfully"
        );
        fetchBusinessTypes(apiPageInfo.currentPage, apiPageInfo.pageSize);
      } catch (err) {
        console.error("Error saving user type:", err);
        dashboardToast.error("Failed to save user type. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [formData, handleClose, fetchBusinessTypes, apiPageInfo.currentPage, apiPageInfo.pageSize]
  );

  const handleDelete = useCallback(
    (businessTypeId) => {
      const businessType = businessTypes.find((bt) => bt.id === businessTypeId);
      if (businessType) {
        setBusinessTypeToDelete(businessType);
        setShowDeleteModal(true);
      }
    },
    [businessTypes]
  );

  const confirmDelete = useCallback(async () => {
    if (!businessTypeToDelete) return;

    try {
      setLoading(true);
      await deleteBusinessType(businessTypeToDelete.id);
      dashboardToast.success("User type deleted successfully");
      fetchBusinessTypes(apiPageInfo.currentPage, apiPageInfo.pageSize);
    } catch (err) {
      console.error("Error deleting user type:", err);
      dashboardToast.error("Failed to delete user type. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setBusinessTypeToDelete(null);
      setLoading(false);
    }
  }, [businessTypeToDelete, fetchBusinessTypes, apiPageInfo.currentPage, apiPageInfo.pageSize]);

  return (
    <div className="sa-settings-panel">
      <SettingsPanelActions>
        <div className="dash-search-field dash-table-search">
          <Search className="dash-search-field__icon" aria-hidden />
          <input
            type="text"
            placeholder="Search by name…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="dash-search-field__input"
            disabled={loading}
          />
        </div>
        <TableSortSelect variant="chip" value={sortKey} onChange={handleSortChange} disabled={loading} />
        <Button variant="primary" className="sa-create-btn" onClick={() => handleShow()}>
          Add user type
        </Button>
      </SettingsPanelActions>

      {loading ? (
        <ContentLoader label="Loading user types…" />
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : (
        <Fragment>
          <div className="app-table-wrap app-table-wrap--refined">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {businessTypes.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center">
                      <span className="app-td__desc">No user types yet.</span>
                    </td>
                  </tr>
                ) : (
                  businessTypes.map((businessType) => {
                    const displayName = businessType.name || "Untitled type";
                    return (
                      <tr
                        key={businessType.id}
                        className="rdm-row-clickable"
                        onClick={() => {
                          setViewBusinessType(businessType);
                          setShowViewModal(true);
                        }}
                      >
                        <td>
                          <span className="app-td__title">{displayName}</span>
                        </td>
                        <td>
                          {businessType.description ? (
                            <span className="app-td__desc" title={businessType.description}>
                              {businessType.description}
                            </span>
                          ) : (
                            <EmptyCell />
                          )}
                        </td>
                        <td>
                          <span
                            className={
                              businessType.isActive
                                ? "app-status app-status--success"
                                : "app-status app-status--muted"
                            }
                          >
                            {businessType.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="dash-mobile-table-actions" onClick={(e) => e.stopPropagation()}>
                          <div className="btn-list-actions">
                            <Button
                              variant="outline"
                              size="sm"
                              className="btn-list-edit"
                              title="Edit"
                              aria-label={`Edit ${displayName}`}
                              onClick={() => handleShow(businessType)}
                            >
                              <Pencil />
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              className="btn-list-delete"
                              title="Delete"
                              aria-label={`Delete ${displayName}`}
                              onClick={() => handleDelete(businessType.id)}
                            >
                              <Trash2 />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <UiPagination
            page={apiPageInfo.currentPage}
            totalPages={apiPageInfo.totalPages}
            onPageChange={goToPage}
          />
        </Fragment>
      )}

      <Modal
        open={showModal}
        onClose={handleClose}
        title={formData.id > 0 ? "Edit user type" : "Add user type"}
        footer={
          <>
            <Button variant="outline" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="user-type-settings-form" disabled={loading}>
              {loading ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        <form id="user-type-settings-form" className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <Input
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter user type name"
          />
          <Textarea
            label="Description"
            name="description"
            rows={3}
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter description"
          />
          <Switch
            label="Active"
            checked={formData.isActive}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({ ...prev, isActive: checked }))
            }
          />
        </form>
      </Modal>

      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete user type"
        footer={
          <>
            <Button variant="outline" type="button" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" type="button" onClick={confirmDelete} disabled={loading}>
              {loading ? "Deleting…" : "Delete"}
            </Button>
          </>
        }
      >
        <p className="m-0 text-sm text-[var(--app-text-muted)]">
          Delete &quot;{businessTypeToDelete?.name}&quot;? This cannot be undone.
        </p>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setViewBusinessType(null);
        }}
        title={viewBusinessType?.name || "User type"}
        subtitle="User type"
        fields={[
          { label: "Name", value: viewBusinessType?.name },
          { label: "Description", value: viewBusinessType?.description, fullWidth: true },
          { label: "Active", value: viewBusinessType?.isActive },
        ]}
        actions={[
          {
            type: "edit",
            onClick: () => {
              const row = viewBusinessType;
              setShowViewModal(false);
              if (row) handleShow(row);
            },
          },
          {
            type: "delete",
            onClick: () => {
              const row = viewBusinessType;
              setShowViewModal(false);
              if (row) handleDelete(row.id);
            },
          },
        ]}
      />
    </div>
  );
};

export default UserTypesPanel;
